import { SitePermitStatus, WorkflowStepType } from '~/client/graph'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Workflow from '~/client/src/shared/models/Workflow'
import WorkflowTypesStore from '~/client/src/shared/stores/domain/WorkflowTypes.store'

/**
 * Returns the forward-action label for a workflow step type.
 * This is the "positive" action only (e.g., Approve, Start, Pass).
 * Returns null for steps that have no forward action button (Request, Submission, RecurringInspection).
 */
export function getForwardActionLabel(
  stepType: WorkflowStepType,
): string | null {
  switch (stepType) {
    case WorkflowStepType.Review:
      return Localization.translator.review_verb
    case WorkflowStepType.Approval:
      return Localization.translator.approve_verb
    case WorkflowStepType.Start:
      return Localization.translator.start_verb
    case WorkflowStepType.OnSite:
      return Localization.translator.onSite
    case WorkflowStepType.ReadyToInspect:
      return Localization.translator.readyToInspect
    case WorkflowStepType.Inspection:
      return Localization.translator.pass_verb
    case WorkflowStepType.Finish:
      return Localization.translator.done
    default:
      return null
  }
}

/**
 * Returns the target SitePermitStatus for the forward action of a workflow step type.
 * If isLastStep is true, the target is always Done.
 * Returns null for steps that have no forward action.
 */
export function getForwardActionTargetStatus(
  stepType: WorkflowStepType,
  isLastStep: boolean,
): SitePermitStatus | null {
  if (isLastStep) {
    return SitePermitStatus.Done
  }

  switch (stepType) {
    case WorkflowStepType.Review:
      return SitePermitStatus.Reviewed
    case WorkflowStepType.Approval:
      return SitePermitStatus.Accepted
    case WorkflowStepType.Start:
      return SitePermitStatus.Active
    case WorkflowStepType.OnSite:
      return SitePermitStatus.OnSite
    case WorkflowStepType.ReadyToInspect:
      return SitePermitStatus.ToInspect
    case WorkflowStepType.Inspection:
      return SitePermitStatus.Passed
    case WorkflowStepType.Finish:
      return SitePermitStatus.Done
    default:
      return null
  }
}

/**
 * Returns true if the current workflow step of the permit has any mandatory fields.
 */
export function currentStepHasMandatoryFields(
  permit: Workflow,
  workflowTypesStore: WorkflowTypesStore,
): boolean {
  const permitType = workflowTypesStore?.getPermitTypeById(permit.typeId)
  if (!permitType) return false

  const step = permitType.workflowSteps.find(
    s => s.id === permit.currentWorkflowStepId,
  )
  if (!step) return false

  return step.fields.some(f => f?.isMandatory === true)
}
